// Name the AI assistant a visit came from — from an explicit attribution
// parameter when one is present, otherwise from the referrer host alone.
//
// WHAT THIS IS. Two pure functions and one that combines them:
//   classifyAiReferrer(referrer)      referrer URL  -> label | null
//   classifyAiUtmSource(search)       ?utm_source=  -> label | null  (host, label, or alias such as `openai`)
//   resolveAiReferrer({ referrer, search })          -> label | null
// GA4 already stores the full referrer and the full page_location (utm
// parameters included) on every page_view, so this adds nothing about the
// visitor; it exists so that "traffic from ChatGPT" is one stable value in
// reports instead of a hostname-or-utm regex maintained in each exploration.
//
// WHY UTM FIRST. ChatGPT search appends `utm_source=chatgpt.com` to the links
// it shows (observed 2026), and an in-app click often arrives with an EMPTY
// referrer. For those visits the utm parameter is the only signal, and it is
// the assistant's own explicit statement of where the click came from, so it
// wins when both are present. Only `utm_source` is read, only when it names a
// recognised assistant; every other utm value is ignored here (it still
// reaches GA4 unchanged through page_location, as before).
//
// WHAT THIS IS NOT. It is not fingerprinting: it reads one query parameter
// and one hostname, nothing else, and stores nothing. Assistants that send
// neither show up as direct traffic here and everywhere else.
// docs/geo/analytics.md records that limitation.
//
// Hosts are matched as exact hostnames or subdomains, never as substrings, so
// `chatgpt.com.example.net` does not count. utm_source values are matched
// after lower-casing, against the same host list plus the bare label.
const AI_REFERRERS = Object.freeze([
  // `aliases` are bare utm_source values an assistant actually appends. OpenAI's
  // web-search citations carry `utm_source=openai` (observed on the 2026-09-06
  // baseline), which is neither a host nor the label, so it is listed here.
  { label: 'chatgpt', hosts: ['chatgpt.com', 'chat.openai.com', 'openai.com'], aliases: ['openai'] },
  { label: 'claude', hosts: ['claude.ai', 'anthropic.com', 'claude.com'] },
  { label: 'perplexity', hosts: ['perplexity.ai'] },
  { label: 'copilot', hosts: ['copilot.microsoft.com', 'bing.com'] },
  { label: 'gemini', hosts: ['gemini.google.com', 'bard.google.com'] },
  { label: 'you', hosts: ['you.com'] },
  { label: 'duckduckgo_ai', hosts: ['duck.ai', 'duckduckgo.com'] },
])

// Bing and DuckDuckGo are search engines first. They are listed because Copilot
// answers and Duck.ai both arrive with those hosts as the referrer, and a
// classic search click from the same host is indistinguishable at this layer.
// Reports should read those two labels as "search-or-assistant", and the
// documentation says so.

function hostMatches(hostname, host) {
  return hostname === host || hostname.endsWith(`.${host}`)
}

function labelForHost(hostname) {
  for (const { label, hosts } of AI_REFERRERS) {
    if (hosts.some((h) => hostMatches(hostname, h))) return label
  }
  return null
}

/**
 * @param {string|null|undefined} referrer  document.referrer (may be empty)
 * @returns {string|null} a label from AI_REFERRERS, or null
 */
export function classifyAiReferrer(referrer) {
  if (!referrer || typeof referrer !== 'string') return null
  let hostname
  try {
    hostname = new URL(referrer).hostname.toLowerCase()
  } catch {
    return null
  }
  if (!hostname) return null
  return labelForHost(hostname)
}

/**
 * The explicit attribution an assistant put on its own link.
 *
 * @param {string|null|undefined} search  window.location.search (may be empty)
 * @returns {string|null} a label from AI_REFERRERS when utm_source names a
 *   recognised assistant (`chatgpt.com`, `chatgpt`, `claude.ai`, …), else null.
 *   Any other utm_source, and every other parameter, is ignored.
 */
export function classifyAiUtmSource(search) {
  if (!search || typeof search !== 'string') return null
  let value
  try {
    value = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('utm_source')
  } catch {
    return null
  }
  if (!value) return null
  const v = value.trim().toLowerCase()
  if (!v) return null
  // A value that is a host or subdomain of a recognised assistant
  // (`chatgpt.com`, `www.perplexity.ai`), or the bare label (`chatgpt`).
  const byHost = labelForHost(v)
  if (byHost) return byHost
  const byLabel = AI_REFERRERS.find((r) => r.label === v || (r.aliases || []).includes(v))
  return byLabel ? byLabel.label : null
}

/**
 * One label for the page load: explicit utm attribution first, referrer host
 * as the fallback. Both inputs optional; null when neither names an assistant.
 */
export function resolveAiReferrer({ referrer, search } = {}) {
  return classifyAiUtmSource(search) ?? classifyAiReferrer(referrer)
}

export const AI_REFERRER_LABELS = Object.freeze(AI_REFERRERS.map((r) => r.label))
